import { Link } from "react-router-dom";
import SiteFooter from "@/components/SiteFooter";
import { Lightning, CaretRight, House } from "@phosphor-icons/react";

/**
 * Shared layout for QuickDukaan static legal / informational pages.
 * Provides: top nav, breadcrumb, optional "Last updated" line, scoped article styles, footer.
 *
 * Props:
 *   title           — H1 string
 *   intro?          — string shown under the H1 (lede)
 *   crumbs          — [{ to, label }] ending with the current page (no link on last)
 *   lastUpdated?    — string (e.g. "21 May 2026")
 *   children        — page sections
 */
export default function LegalLayout({ title, intro, crumbs = [], lastUpdated, children, testid }) {
  return (
    <div className="min-h-screen bg-white text-gray-900 antialiased">
      {/* Top nav — keeps brand visible without competing with footer links */}
      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-14 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2" data-testid="legal-nav-logo">
            <span className="w-8 h-8 rounded-lg bg-[#F8CB46] flex items-center justify-center">
              <Lightning weight="fill" size={16} />
            </span>
            <span className="font-heading font-black tracking-tight">QuickDukaan</span>
          </Link>
          <nav className="hidden sm:flex items-center gap-5 text-sm">
            <Link to="/QDplatform" className="text-gray-700 hover:text-[#15803D] transition">Platform</Link>
            <Link to="/contact-us" className="text-gray-700 hover:text-[#15803D] transition">Contact</Link>
            <Link to="/seller/login" className="bg-[#15803D] text-white font-bold px-3 py-1.5 rounded-full text-xs hover:bg-[#166534] transition">Become a seller</Link>
          </nav>
        </div>
      </header>

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="max-w-3xl mx-auto px-5 sm:px-8 pt-6">
        <ol className="flex flex-wrap items-center gap-1.5 text-xs text-gray-500" data-testid="legal-breadcrumb">
          <li>
            <Link to="/" className="inline-flex items-center gap-1 hover:text-[#15803D] transition">
              <House size={12} weight="duotone" /> Home
            </Link>
          </li>
          {crumbs.map((c, i) => {
            const last = i === crumbs.length - 1;
            return (
              <li key={i} className="inline-flex items-center gap-1.5">
                <CaretRight size={10} className="text-gray-400" />
                {last || !c.to ? (
                  <span aria-current={last ? "page" : undefined} className="text-gray-700 font-semibold">{c.label}</span>
                ) : (
                  <Link to={c.to} className="hover:text-[#15803D] transition">{c.label}</Link>
                )}
              </li>
            );
          })}
        </ol>
      </nav>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-6 sm:py-10 fade-up" data-testid={testid || "legal-page"}>
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.1]">
          {title}
        </h1>
        {intro && (
          <p className="mt-4 text-base sm:text-lg text-gray-600 leading-relaxed">
            {intro}
          </p>
        )}
        {lastUpdated && (
          <div className="mt-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#15803D] bg-[#15803D]/10 px-3 py-1.5 rounded-full" data-testid="legal-last-updated">
            Last Updated: {lastUpdated}
          </div>
        )}

        <hr className="my-8 border-gray-100" />

        <article className="legal-prose">
          {children}
        </article>

        <hr className="mt-12 mb-6 border-gray-100" />
        <p className="text-sm text-gray-500">
          Have a question about this page? Email us at{" "}
          <a href="mailto:support@quickdukaan.in" className="text-[#15803D] font-bold underline-offset-2 hover:underline">
            support@quickdukaan.in
          </a>.
        </p>
      </main>

      <SiteFooter />
    </div>
  );
}

/* Page-section helpers */
export function Section({ number, title, children }) {
  return (
    <section className="mt-8 first:mt-0 scroll-mt-20" id={`section-${number}`}>
      <h2 className="font-heading font-black text-xl sm:text-2xl tracking-tight">
        <span className="text-[#15803D] mr-2 tabular-nums">{String(number).padStart(2, "0")}.</span>
        {title}
      </h2>
      <div className="mt-3 text-[15px] leading-relaxed text-gray-700 space-y-3">
        {children}
      </div>
    </section>
  );
}

export function Bullets({ items }) {
  return (
    <ul className="space-y-1.5 pl-5 list-disc marker:text-[#15803D]">
      {items.map((t, i) => <li key={i}>{t}</li>)}
    </ul>
  );
}
